import React, { useState } from "react";
import { useQuery } from "@tanstack/react-query";
import { api, inr } from "@/lib/api";
import { ROLE_LABEL } from "@/lib/auth";
import { Input } from "@/components/ui/input";
import { Badge } from "@/components/ui/badge";
import { ResponsiveContainer, BarChart, Bar, XAxis, YAxis, Tooltip, CartesianGrid } from "recharts";

function Section({ title, children }) {
  return (
    <div className="bg-white border border-[#E4E4E7] rounded-xl p-5">
      <h3 className="font-display font-bold text-[#06152D] mb-3">{title}</h3>
      {children}
    </div>
  );
}

export default function Reports() {
  const now = new Date();
  const [month, setMonth] = useState(now.getMonth() + 1);
  const [year, setYear] = useState(now.getFullYear());

  const { data: leadsRep } = useQuery({ queryKey: ["rep-leads"], queryFn: async () => (await api.get("/reports/leads-report")).data });
  const { data: closedRep } = useQuery({ queryKey: ["rep-closed"], queryFn: async () => (await api.get("/reports/closed-loans")).data });
  const { data: pendingRep } = useQuery({ queryKey: ["rep-pending"], queryFn: async () => (await api.get("/reports/pending")).data });
  const { data: insRep } = useQuery({ queryKey: ["rep-ins"], queryFn: async () => (await api.get("/reports/insurance-report")).data });
  const { data: empWiseRep } = useQuery({ queryKey: ["rep-emp"], queryFn: async () => (await api.get("/reports/employee-wise")).data });
  const { data: branchRep } = useQuery({ queryKey: ["rep-branch"], queryFn: async () => { try { return (await api.get("/reports/branch-wise")).data; } catch { return []; } } });
  const { data: monthRep } = useQuery({ queryKey: ["rep-month", month, year], queryFn: async () => (await api.get("/reports/monthly", { params: { month, year } })).data });
  const { data: yearRep } = useQuery({ queryKey: ["rep-year", year], queryFn: async () => (await api.get("/reports/yearly", { params: { year } })).data });

  return (
    <div>
      <h1 className="font-display text-3xl font-bold tracking-tight text-[#06152D]">Reports</h1>
      <p className="text-sm text-[#52525B]">Branch-wise, Leads, Closed Loans, Pending, Insurance, Employee-wise, Monthly, Yearly.</p>

      {/* Row 1: Leads + Pending + Insurance */}
      <div className="mt-6 grid lg:grid-cols-3 gap-4">
        <Section title="Leads Report">
          <div className="text-3xl font-bold font-mono-num text-[#06152D]">{leadsRep?.total ?? 0}</div>
          <div className="text-xs text-[#52525B] mb-3">Total leads · {leadsRep?.conversion_rate ?? 0}% conversion</div>
          <div className="space-y-1 text-xs">
            {(leadsRep?.by_status || []).map((s) => (
              <div key={s.key} className="flex justify-between"><span>{s.key}</span><span className="font-mono-num font-semibold">{s.count}</span></div>
            ))}
          </div>
        </Section>
        <Section title="Pending Report">
          <div className="space-y-2">
            <div className="flex justify-between"><span className="text-sm text-[#52525B]">Loans Pending</span><span className="font-mono-num font-bold text-amber-700">{pendingRep?.pending_loans ?? 0}</span></div>
            <div className="flex justify-between"><span className="text-sm text-[#52525B]">Follow-ups Pending</span><span className="font-mono-num font-bold text-amber-700">{pendingRep?.pending_followups ?? 0}</span></div>
            <div className="flex justify-between"><span className="text-sm text-[#52525B]">Leads Pending</span><span className="font-mono-num font-bold text-amber-700">{pendingRep?.pending_leads ?? 0}</span></div>
          </div>
        </Section>
        <Section title="Insurance Report">
          <div className="text-3xl font-bold font-mono-num text-[#06152D]">{insRep?.total ?? 0}</div>
          <div className="text-xs text-[#52525B] mb-2">Total policies · {insRep?.renewals_due_30d ?? 0} renewals in 30 days</div>
          <div className="space-y-1 text-xs">
            {(insRep?.by_policy_type || []).map((s) => (
              <div key={s.key} className="flex justify-between"><span>{s.key || "—"}</span><span className="font-mono-num font-semibold">{s.count}</span></div>
            ))}
          </div>
        </Section>
      </div>

      {/* Row 2: Closed Loans + Employee-wise */}
      <div className="mt-4 grid lg:grid-cols-2 gap-4">
        <Section title="Closed / Disbursed Loans">
          <div className="text-3xl font-bold font-mono-num text-emerald-700">{closedRep?.count ?? 0}</div>
          <div className="text-xs text-[#52525B]">Total disbursed: <span className="font-semibold">{inr(closedRep?.total_disbursed || 0)}</span></div>
          <div className="mt-3 max-h-64 overflow-y-auto">
            <table className="w-full lj-table text-xs" data-testid="closed-loans-table">
              <thead><tr><th>File</th><th>Amount</th><th>Status</th></tr></thead>
              <tbody>
                {(closedRep?.loans || []).slice(0, 20).map((l) => (
                  <tr key={l.id}><td className="font-mono-num">{l.file_number || "—"}</td><td className="font-mono-num">{inr(l.disbursed_amount || l.amount)}</td><td><Badge variant="secondary">{l.status}</Badge></td></tr>
                ))}
              </tbody>
            </table>
          </div>
        </Section>
        <Section title="Employee-wise Report">
          <div className="overflow-x-auto">
            <table className="w-full lj-table text-xs" data-testid="emp-wise-table">
              <thead><tr><th>Employee</th><th>Branch</th><th>Leads</th><th>Conv.</th><th>FU Done</th><th>Rate</th></tr></thead>
              <tbody>
                {(empWiseRep || []).map((p) => (
                  <tr key={p.user_id}>
                    <td>{p.name} <span className="text-[10px] text-[#52525B]">{p.employee_id || "—"}</span></td>
                    <td>{p.branch || "—"}</td>
                    <td className="font-mono-num">{p.leads}</td>
                    <td className="font-mono-num">{p.converted}</td>
                    <td className="font-mono-num">{p.followups_done}</td>
                    <td className="font-mono-num">{p.conversion_rate}%</td>
                  </tr>
                ))}
                {(!empWiseRep || empWiseRep.length === 0) && <tr><td colSpan="6" className="text-center text-[#52525B] py-4">No data.</td></tr>}
              </tbody>
            </table>
          </div>
        </Section>
      </div>

      {/* Row 3: Branch-wise */}
      {branchRep && branchRep.length > 0 && (
        <div className="mt-4">
          <Section title="Branch-wise Report">
            <div className="overflow-x-auto">
              <table className="w-full lj-table text-xs" data-testid="branch-wise-table">
                <thead><tr><th>Branch</th><th>Leads</th><th>Customers</th><th>Loans</th><th>Insurance</th><th>Employees</th></tr></thead>
                <tbody>
                  {branchRep.map((b) => (
                    <tr key={b.branch}>
                      <td className="font-semibold">{b.branch}</td>
                      <td className="font-mono-num">{b.leads}</td>
                      <td className="font-mono-num">{b.customers}</td>
                      <td className="font-mono-num">{b.loans}</td>
                      <td className="font-mono-num">{b.insurance}</td>
                      <td className="font-mono-num">{b.employees}</td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
          </Section>
        </div>
      )}

      {/* Row 4: Monthly + Yearly */}
      <div className="mt-4 grid lg:grid-cols-2 gap-4">
        <Section title="Monthly Report">
          <div className="flex gap-2 mb-3">
            <Input type="number" min="1" max="12" value={month} onChange={(e) => setMonth(parseInt(e.target.value || "1"))} className="w-20" data-testid="report-month" />
            <Input type="number" min="2020" max="2100" value={year} onChange={(e) => setYear(parseInt(e.target.value || "2026"))} className="w-24" data-testid="report-year" />
          </div>
          {monthRep && (
            <div className="grid grid-cols-2 gap-3 text-sm">
              <div>Leads: <span className="font-mono-num font-bold">{monthRep.leads}</span></div>
              <div>Converted: <span className="font-mono-num font-bold">{monthRep.converted}</span></div>
              <div>Customers: <span className="font-mono-num font-bold">{monthRep.customers}</span></div>
              <div>Loans: <span className="font-mono-num font-bold">{monthRep.loans}</span></div>
              <div>Disbursed: <span className="font-mono-num font-bold">{monthRep.disbursed}</span></div>
            </div>
          )}
        </Section>
        <Section title={`Yearly Report · ${year}`}>
          <div className="h-56">
            <ResponsiveContainer width="100%" height="100%">
              <BarChart data={(yearRep?.months || []).map((m) => ({ name: `M${m.month}`, leads: m.leads, converted: m.converted }))}>
                <CartesianGrid strokeDasharray="3 3" stroke="#E4E4E7" />
                <XAxis dataKey="name" />
                <YAxis allowDecimals={false} />
                <Tooltip />
                <Bar dataKey="leads" fill="#0052FF" radius={[6, 6, 0, 0]} />
                <Bar dataKey="converted" fill="#10B981" radius={[6, 6, 0, 0]} />
              </BarChart>
            </ResponsiveContainer>
          </div>
        </Section>
      </div>
    </div>
  );
}
